import React, { memo, useEffect, useRef } from "react";
import clsx from "clsx";
import styles from "./Swatch.module.scss";

type Props = {
    className?: string;
    variant?: "default" | "md" | "sm";
    fill?: string;
    image?: string;
    active?: boolean;
    hasGradient?: boolean;
} & React.HTMLProps<HTMLDivElement>;

const Swatch: React.FC<Props> = ({ variant = "default", fill, image, active, hasGradient, className, ...rest }) => {
    const imgRef = useRef<HTMLImageElement>(null);

    // Using useEffect to hide the image if it fails to load.
    // This is used instead of onError on the img tag to prevent race conditions.
    useEffect(() => {
        if (!image || !imgRef.current) return;

        const img = imgRef.current;

        const handleError = () => {
            img.style.display = "none";
        };

        img.addEventListener("error", handleError);

        if (img.complete && img.naturalWidth === 0) {
            handleError();
        }

        return () => {
            img.removeEventListener("error", handleError);
        };
    }, [image]);
    return (
        <div
            className={clsx(
                styles.swatch,
                styles[variant],
                hasGradient && styles["has-gradient"],
                active && styles.active,
                className,
            )}
            style={{ backgroundColor: fill ?? "transparent" }}
            {...rest}
        >
            {image && (
                <img
                    ref={imgRef}
                    src={image}
                    width={48}
                    height={48}
                    loading="lazy"
                    data-decorative="yes"
                    alt=""
                    role="presentation"
                />
            )}
        </div>
    );
};

export default memo(Swatch);
